import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ConnectionStatus from './ConnectionStatus';
import ClipboardInput from './ClipboardInput';
import ClipboardItem from './ClipboardItem';
import { socketService } from '../services/socketService';
import { roomService } from '../services/roomService';
import ExitConfirmationModal from './ExitConfirmationModal';
import ExpirationBanner from './ExpirationBanner';
import toast from 'react-hot-toast';

const { FiLogOut, FiRefreshCw, FiShare2, FiTrash2 } = FiIcons;

const ClipboardRoom = ({ roomName, password, onLeaveRoom }) => {
  const [clipboardItems, setClipboardItems] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [userCount, setUserCount] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isClearing, setIsClearing] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(60); // Minutes remaining before expiration
  const [connectedDevices, setConnectedDevices] = useState([]);
  const deviceId = socketService.getDeviceId();

  // Use a ref to track if we've already set up event listeners
  const hasSetupListeners = useRef(false);
  // Ref to track current room
  const currentRoomRef = useRef(roomName);
  // Ref to track if room verification is complete
  const roomVerifiedRef = useRef(false);

  // Set up expiration timer check - but only after initial room verification
  useEffect(() => {
    // Wait a bit before starting expiration checks to allow room creation to complete
    const startExpirationCheck = setTimeout(() => {
      const checkExpiration = async () => {
        // Don't check expiration until room is verified
        if (!roomVerifiedRef.current) {
          return;
        }

        try {
          // First check if the room still exists
          const roomExistsResult = await roomService.checkRoomExists(roomName);
          if (!roomExistsResult.exists) {
            // Room has been deleted (expired)
            console.log('[ClipboardRoom] Room no longer exists');
            toast.error('This room has expired and been deleted');
            socketService.disconnect();
            onLeaveRoom();
            return;
          }

          // Get room expiration time if room exists
          const { success, timeRemaining, isExpired } = await roomService.getRoomExpiration(roomName);
          if (success) {
            if (isExpired) {
              // Room has expired
              console.log('[ClipboardRoom] Room has expired');
              toast.error('This room has expired');
              socketService.disconnect();
              onLeaveRoom();
            } else {
              // Update time remaining
              setTimeRemaining(timeRemaining);
            }
          }
        } catch (error) {
          console.error('[ClipboardRoom] Error checking room expiration:', error);
        }
      };

      // Initial check after delay
      checkExpiration();

      // Set up interval to check expiration
      const expirationInterval = setInterval(checkExpiration, 30000); // Check every 30 seconds

      // Update the countdown timer every minute
      const countdownInterval = setInterval(() => {
        setTimeRemaining(prev => {
          const newValue = prev - 1;
          if (newValue <= 0) {
            // Room expired, disconnect and return to home
            toast.error('Room has expired');
            socketService.disconnect();
            onLeaveRoom();
            return 0;
          }
          return newValue;
        });
      }, 60 * 1000);

      return () => {
        clearInterval(expirationInterval);
        clearInterval(countdownInterval);
      };
    }, 2000); // Wait 2 seconds before starting expiration checks

    return () => {
      clearTimeout(startExpirationCheck);
    };
  }, [roomName, onLeaveRoom]);

  // Initialize socket connection
  useEffect(() => {
    console.log(`[ClipboardRoom] Initializing connection for room: ${roomName}`);

    // Update room ref
    currentRoomRef.current = roomName;

    const initializeRoom = async () => {
      try {
        // First verify the room exists and we can access it
        console.log('[ClipboardRoom] Verifying room access...');
        const verifyResult = await roomService.verifyRoom(roomName, password);
        
        if (!verifyResult.success) {
          console.error('[ClipboardRoom] Room verification failed:', verifyResult.error);
          toast.error(verifyResult.error || 'Failed to access room');
          onLeaveRoom();
          return;
        }

        console.log('[ClipboardRoom] Room verification successful');
        roomVerifiedRef.current = true;

        // Now proceed with socket connection
        const socket = socketService.connect();

        // Clean up function to properly handle component unmount
        const cleanupListeners = () => {
          if (socket) {
            console.log('[ClipboardRoom] Cleaning up socket listeners');
            socket.off('connect');
            socket.off('disconnect');
            socket.off('user_count_update');
            socket.off('clipboard_sync');
            socket.off('new_clipboard_item');
            socket.off('user_left');
            socket.off('room_expired');
            socket.off('connected_devices');
          }
        };

        // Set up event listeners if we haven't already
        if (!hasSetupListeners.current) {
          console.log('[ClipboardRoom] Setting up socket event listeners');

          socket.on('connect', () => {
            console.log('[ClipboardRoom] Socket connected');
            setIsConnected(true);
            setIsLoading(false);
            
            // Join the room
            socket.emit('join_room', { room: roomName, password });
            toast.success('Connected to room');
          });

          socket.on('disconnect', () => {
            console.log('[ClipboardRoom] Socket disconnected');
            setIsConnected(false);
            toast.error('Disconnected from room');
          });

          socket.on('user_count_update', (data) => {
            setUserCount(data.count);
          });

          socket.on('clipboard_sync', (items) => {
            console.log('[ClipboardRoom] Received clipboard sync:', items);
            if (items && Array.isArray(items)) {
              // Make sure we only update for the current room
              if (currentRoomRef.current === roomName) {
                setClipboardItems(items);
              }
            } else {
              console.log('[ClipboardRoom] Received empty or invalid clipboard sync');
              setClipboardItems([]);
            }
          });

          socket.on('new_clipboard_item', (item) => {
            console.log('[ClipboardRoom] Received new clipboard item:', item);
            // Make sure we only update for the current room
            if (currentRoomRef.current === roomName && item.room_name === roomName) {
              setClipboardItems(prev => {
                // Check if the item is already in the list to avoid duplicates
                const exists = prev.some(existingItem => 
                  existingItem.id === item.id || 
                  (existingItem.content === item.content && 
                   existingItem.device_name === item.device_name && 
                   Math.abs(new Date(existingItem.timestamp) - new Date(item.timestamp)) < 1000)
                );

                if (exists) {
                  console.log('[ClipboardRoom] Item already exists, not adding duplicate');
                  return prev;
                }

                console.log('[ClipboardRoom] Adding new item to clipboard items');
                return [item, ...prev];
              });

              // Only show toast for items from other devices
              if (item.device_name !== deviceId) {
                toast.success('New clipboard item received');
              }
            }
          });

          socket.on('user_left', (data) => {
            console.log('[ClipboardRoom] User left:', data);
            // Could show notification that someone left
            toast.info(`Another device disconnected from the room`);
          });

          socket.on('room_expired', () => {
            console.log('[ClipboardRoom] Room expired');
            toast.error('This room has expired and been deleted');
            socketService.disconnect();
            onLeaveRoom();
          });

          socket.on('connected_devices', (devices) => {
            console.log('[ClipboardRoom] Connected devices update:', devices);
            setConnectedDevices(devices);
          });

          hasSetupListeners.current = true;
        }

        // Join room if we're not already in it
        if (socketService.getCurrentRoom() !== roomName) {
          console.log('[ClipboardRoom] Joining room:', roomName);
          socket.emit('join_room', { room: roomName, password });
        }

        // Start simulation for demo
        socketService.simulateRealTime();

        return cleanupListeners;

      } catch (error) {
        console.error('[ClipboardRoom] Error initializing room:', error);
        toast.error('Failed to initialize room');
        setIsLoading(false);
        onLeaveRoom();
      }
    };

    // Initialize room with a small delay to ensure everything is ready
    const initTimeout = setTimeout(initializeRoom, 500);

    return () => {
      clearTimeout(initTimeout);
    };
  }, [roomName, password, deviceId, onLeaveRoom]);

  const handleAddClipboardItem = useCallback(async (content) => {
    console.log('[ClipboardRoom] Adding clipboard item:', content);

    try {
      // First check if the room still exists
      const roomExistsResult = await roomService.checkRoomExists(roomName);
      if (!roomExistsResult.exists) {
        // Room has been deleted (expired)
        toast.error('This room has expired and been deleted');
        socketService.disconnect();
        onLeaveRoom();
        return false;
      }

      // Create new item object
      const newItem = {
        content,
        deviceName: deviceId,
        timestamp: new Date().toISOString()
      };

      // Add to local state immediately for better UX
      const tempItem = {
        id: `temp-${Date.now()}`,
        content: newItem.content,
        device_name: newItem.deviceName,
        timestamp: newItem.timestamp,
        room_name: roomName
      };

      setClipboardItems(prev => [tempItem, ...prev]);

      // Update room activity when adding items
      await roomService.updateRoomActivity(roomName);

      // Emit to socket (which will save to Supabase)
      const socket = socketService.getSocket();
      if (socket) {
        console.log('[ClipboardRoom] Emitting add_clipboard_item event');
        socket.emit('add_clipboard_item', { room: roomName, item: newItem });
      } else {
        console.error('[ClipboardRoom] Socket not available');
        toast.error('Failed to add clipboard item: No connection');
      }

      return true;
    } catch (error) {
      console.error('[ClipboardRoom] Error adding clipboard item:', error);
      toast.error('Failed to add clipboard item');
      return false;
    }
  }, [roomName, deviceId, onLeaveRoom]);

  const handleClearClipboard = useCallback(async () => {
    console.log('[ClipboardRoom] Clearing clipboard items');

    if (!isConnected) {
      toast.error('Not connected to room');
      return;
    }

    setIsClearing(true);

    try {
      // First check if the room still exists
      const roomExistsResult = await roomService.checkRoomExists(roomName);
      if (!roomExistsResult.exists) {
        // Room has been deleted (expired)
        toast.error('This room has expired and been deleted');
        socketService.disconnect();
        onLeaveRoom();
        return;
      }

      const socket = socketService.getSocket();
      if (socket) {
        console.log('[ClipboardRoom] Emitting clear_clipboard_items event');
        socket.emit('clear_clipboard_items', { room: roomName });
        toast.success('Clipboard cleared');
      } else {
        console.error('[ClipboardRoom] Socket not available');
        toast.error('Failed to clear clipboard: No connection');
      }
    } catch (error) {
      console.error('[ClipboardRoom] Error clearing clipboard:', error);
      toast.error('Failed to clear clipboard');
    } finally {
      setIsClearing(false);
    }
  }, [roomName, isConnected, onLeaveRoom]);

  const handleInitiateLeave = () => {
    // If we have items in the clipboard, show confirmation dialog
    if (clipboardItems.length > 0) {
      setShowExitConfirmation(true);
    } else {
      // No items, so just leave
      handleConfirmLeave();
    }
  };

  const handleConfirmLeave = () => {
    socketService.disconnect();
    onLeaveRoom();
    toast.success('Left room');
  };

  const handleCancelLeave = () => {
    setShowExitConfirmation(false);
  };

  const handleShareRoom = async () => {
    const shareText = `Join my ClipShares room!\nRoom: ${roomName}\nPassword: ${password}\nNote: This room will automatically expire in ${timeRemaining} minutes.`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'ClipShares Room',
          text: shareText
        });
      } catch (error) {
        // Fallback to clipboard
        await navigator.clipboard.writeText(shareText);
        toast.success('Room details copied to clipboard');
      }
    } else {
      await navigator.clipboard.writeText(shareText);
      toast.success('Room details copied to clipboard');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <SafeIcon icon={FiRefreshCw} className="w-8 h-8 text-primary-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Connecting to room...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        {/* Expiration Banner */}
        <ExpirationBanner timeRemaining={timeRemaining} />

        {/* Header */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6 mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">ClipShares Room</h1>
              <p className="text-gray-600">
                Room: <span className="font-mono font-medium">{roomName}</span>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleShareRoom}
                className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                title="Share room details"
              >
                <SafeIcon icon={FiShare2} className="w-5 h-5" />
              </button>
              <button
                onClick={handleInitiateLeave}
                className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                title="Leave room"
              >
                <SafeIcon icon={FiLogOut} className="w-5 h-5" />
              </button>
            </div>
          </div>

          <ConnectionStatus
            isConnected={isConnected}
            userCount={userCount}
            deviceId={deviceId}
            connectedDevices={connectedDevices}
          />
        </motion.div>

        {/* Clipboard Input */}
        <div className="mb-8">
          <ClipboardInput
            onAddClipboardItem={handleAddClipboardItem}
            isConnected={isConnected}
          />
        </div>

        {/* Clipboard Items */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Shared Clipboard Items ({clipboardItems.length})
            </h2>

            {/* Clear All Button - Only shown when there are items */}
            <AnimatePresence>
              {clipboardItems.length > 0 && (
                <motion.button
                  onClick={handleClearClipboard}
                  className="flex items-center gap-2 px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Clear all clipboard items"
                  disabled={isClearing || !isConnected}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <SafeIcon
                    icon={isClearing ? FiRefreshCw : FiTrash2}
                    className={`w-4 h-4 ${isClearing ? 'animate-spin' : ''}`}
                  />
                  <span className="text-sm font-medium">Clear All</span>
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          <AnimatePresence>
            {clipboardItems.length === 0 ? (
              <motion.div
                className="text-center py-12"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiShare2} className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 text-lg mb-2">No items shared yet</p>
                <p className="text-gray-400 text-sm">
                  Add content above to start sharing with connected devices
                </p>
              </motion.div>
            ) : (
              <div className="space-y-4">
                {clipboardItems.map((item, index) => (
                  <ClipboardItem
                    key={item.id || `item-${index}`}
                    item={item}
                    index={index}
                  />
                ))}
              </div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Exit Confirmation Modal */}
      <ExitConfirmationModal
        isOpen={showExitConfirmation}
        onClose={handleCancelLeave}
        onConfirm={handleConfirmLeave}
        itemCount={clipboardItems.length}
      />
    </div>
  );
};

export default ClipboardRoom;